import { useMemo } from 'react';
import { useMutation, useQuery, useQueries } from '@tanstack/react-query';
import { useSelector } from 'react-redux';

import type {
  ZuubOnboardingBeginRequest,
  ZuubOnboardingBeginResponse,
  ZuubOnboardingStatusResponse,
  ZuubPayer,
} from '@/@types/zuub/contracts';

import { makeAPIRequestReactQueryWithHeaders } from '../api';
import { endpoints } from '../endpoints';

// Hook to get list of payers that support portal connections
export const useZuubRcmOnboardingPayers = () => {
  const {
    primaryPracticeId: currentPractice,
    primaryOrganizationId: currentOrg,
  } = useSelector(
    (state: {
      login: {
        user: {
          primaryPracticeId?: string;
          primaryOrganizationId?: string;
        };
      };
    }) => state.login.user,
  );

  return useQuery<ZuubPayer[]>(
    [endpoints.rcmZuubOnboarding.payers],
    () =>
      makeAPIRequestReactQueryWithHeaders<ZuubPayer[]>(
        endpoints.rcmZuubOnboarding.payers,
        undefined,
        {
          Org: currentOrg,
          Practice: currentPractice,
        },
        'GET',
      ),
    {
      enabled: Boolean(currentPractice && currentOrg),
      staleTime: 5 * 60 * 1000, // 5 minutes
    },
  );
};

// Hook to begin onboarding process
export const useZuubRcmOnboardingBegin = () => {
  const {
    primaryPracticeId: currentPractice,
    primaryOrganizationId: currentOrg,
  } = useSelector(
    (state: {
      login: {
        user: {
          primaryPracticeId?: string;
          primaryOrganizationId?: string;
        };
      };
    }) => state.login.user,
  );

  return useMutation<
    ZuubOnboardingBeginResponse,
    Error,
    ZuubOnboardingBeginRequest
  >((data) =>
    makeAPIRequestReactQueryWithHeaders<ZuubOnboardingBeginResponse>(
      endpoints.rcmZuubOnboarding.begin,
      undefined,
      {
        Org: currentOrg,
        Practice: currentPractice,
      },
      'POST',
      {
        data,
      },
    ),
  );
};

// Hook to check onboarding status with polling
export const useZuubRcmOnboardingStatus = (
  payerId: number,
  enabled: boolean = true,
) => {
  const {
    primaryPracticeId: currentPractice,
    primaryOrganizationId: currentOrg,
  } = useSelector(
    (state: {
      login: {
        user: {
          primaryPracticeId?: string;
          primaryOrganizationId?: string;
        };
      };
    }) => state.login.user,
  );

  return useQuery<ZuubOnboardingStatusResponse>(
    [endpoints.rcmZuubOnboarding.status, payerId],
    () =>
      makeAPIRequestReactQueryWithHeaders<ZuubOnboardingStatusResponse>(
        `${endpoints.rcmZuubOnboarding.status}/${payerId}`,
        undefined,
        {
          Org: currentOrg,
          Practice: currentPractice,
        },
        'GET',
      ),
    {
      enabled: Boolean(currentPractice && currentOrg && enabled),
      refetchInterval: (data) => {
        // Stop polling if status is Succeeded or Failed
        if (
          data?.credentialStatus === 'Succeeded' ||
          data?.credentialStatus === 'Failed'
        ) {
          return false;
        }
        // Poll every 3 seconds for other statuses
        return 3000;
      },
      staleTime: 0, // Always consider data stale to enable polling
    },
  );
};

// Hook to check individual payer statuses using useQueries
// This replaces the batch endpoint with individual /rcm/onboarding/status/payerId calls
// On page load, fetches all payer statuses once (no polling)
// Only polls specific payers that are in the pollingPayerIds set (e.g., when user clicks "Test connection")
export const useZuubRcmOnboardingStatuses = (
  payerIds: number[],
  enabled: boolean = true,
  pollingPayerIds: Set<number> = new Set(),
) => {
  const {
    primaryPracticeId: currentPractice,
    primaryOrganizationId: currentOrg,
  } = useSelector(
    (state: {
      login: {
        user: {
          primaryPracticeId?: string;
          primaryOrganizationId?: string;
        };
      };
    }) => state.login.user,
  );

  // Memoize payerIds to prevent unnecessary query recreations
  // Only recreate when the actual payer IDs change (not just the array reference)
  const payerIdsKey = useMemo(() => payerIds.join(','), [payerIds]);
  const stablePayerIds = useMemo(() => {
    const sorted = [...payerIds].sort((a, b) => a - b);
    return sorted;
  }, [payerIds]); // payerIdsKey is derived from payerIds, so it's redundant

  // Convert Set to sorted array string for stable comparison
  // This prevents recreating queries when Set reference changes but contents are the same
  const pollingPayerIdsKey = useMemo(() => {
    return Array.from(pollingPayerIds)
      .sort((a, b) => a - b)
      .join(',');
  }, [pollingPayerIds]);

  // Create a stable Set from the key for lookups
  // Only recreate when the actual contents change (when key changes)
  const pollingPayerIdsSet = useMemo(() => {
    if (!pollingPayerIdsKey) return new Set<number>();
    return new Set(pollingPayerIdsKey.split(',').map((id) => parseInt(id, 10)));
  }, [pollingPayerIdsKey]);

  // Memoize queries configuration to prevent unnecessary recreations
  // Only recreate when payerIds, polling state, or other dependencies actually change
  const queriesConfig = useMemo(() => {
    return stablePayerIds.map((payerId) => {
      // Check polling state using the stable set
      const shouldPoll = pollingPayerIdsSet.has(payerId);
      return {
        queryKey: [
          endpoints.rcmZuubOnboarding.status,
          payerId,
          // Stable query key - only based on payerId, not polling state
        ],
        queryFn: () =>
          makeAPIRequestReactQueryWithHeaders<ZuubOnboardingStatusResponse>(
            `${endpoints.rcmZuubOnboarding.status}/${payerId}`,
            undefined,
            {
              Org: currentOrg,
              Practice: currentPractice,
            },
            'GET',
          ),
        enabled: Boolean(currentPractice && currentOrg && enabled),
        // Prevent unnecessary refetches for non-polling queries
        refetchOnMount: false, // Never refetch on mount - data is fresh or we explicitly refetch
        refetchOnWindowFocus: false,
        refetchOnReconnect: false,
        retry: false, // Disable retries to prevent duplicate requests
        retryOnMount: false, // Don't retry on mount
        refetchInterval: (data: unknown) => {
          const statusData = data as ZuubOnboardingStatusResponse | undefined;
          const status = statusData?.credentialStatus;

          // Stop polling if status is Succeeded or Failed
          if (status === 'Succeeded' || status === 'Failed') {
            return false;
          }

          // Poll if:
          // 1. Payer is in pollingPayerIds (user clicked "Test connection"), OR
          // 2. Status is Acknowledged or InProgress (already connecting)
          // Note: If data is not yet loaded (status is undefined), we still poll if payer is in polling set
          const isInPollingSet = pollingPayerIdsSet.has(payerId);
          const isInProgress =
            status === 'Acknowledged' || status === 'InProgress';

          // If data is not loaded yet, poll if payer is in polling set
          // Otherwise, poll if in polling set OR status is in-progress
          if (status === undefined) {
            return isInPollingSet ? 3000 : false;
          }

          if (isInPollingSet || isInProgress) {
            // Poll every 3 seconds
            return 3000;
          }

          return false;
        },
        staleTime: shouldPoll ? 0 : Infinity, // Non-polling queries: never consider stale to prevent refetches
        gcTime: 30 * 60 * 1000, // Keep in cache for 30 minutes
      };
    });
  }, [
    stablePayerIds,
    pollingPayerIdsSet,
    currentPractice,
    currentOrg,
    enabled,
  ]);

  const queries = useQueries({
    queries: queriesConfig,
  });

  // Transform the queries array into a map of payerId -> status response
  const data: Record<string, ZuubOnboardingStatusResponse> | undefined =
    useMemo(() => {
      if (!queries.length) return undefined;

      const result: Record<string, ZuubOnboardingStatusResponse> = {};
      queries.forEach((query, index) => {
        const payerId = stablePayerIds[index];
        if (query.data) {
          result[payerId.toString()] = query.data;
        }
      });
      return result;
    }, [queries, stablePayerIds]);

  // Determine if any query is loading
  const isLoading = queries.some((query) => query.isLoading);

  // Determine if any query has an error
  const isError = queries.some((query) => query.isError);

  // Create a map of payerId -> loading state for individual loading indicators
  const loadingStates = useMemo(() => {
    const loadingMap: Record<string, boolean> = {};
    queries.forEach((query, index) => {
      const payerId = stablePayerIds[index];
      loadingMap[payerId.toString()] = query.isLoading;
    });
    return loadingMap;
  }, [queries, stablePayerIds]);

  // Get refetch function that refetches all queries
  const refetch = () => {
    return Promise.all(queries.map((query) => query.refetch()));
  };

  // Get refetch function for a specific payer
  const refetchPayer = (payerId: number) => {
    const payerIndex = stablePayerIds.indexOf(payerId);
    if (payerIndex === -1 || !queries[payerIndex]) {
      return Promise.resolve(undefined);
    }
    return queries[payerIndex].refetch();
  };

  return {
    data,
    isLoading,
    isError,
    loadingStates, // Map of payerId -> loading state
    refetch, // Refetch all payers
    refetchPayer, // Refetch a specific payer
  };
};
